import { memo, useId } from "react";
import { Box, Card, CardContent, Divider, Grid, Tooltip, Typography } from "@mui/material";
import { formatValue } from "components/ui/formatters";
import { SingleValueCardColor } from "constants/index";
import { MultiValuesCardProps } from "helpers/types";
import EditableTextField from "./EditableTextField";
import {
  UNAVAILABLE_DISPLAY,
  UnavailableReasonIcon,
  isPlaceholderDisplay,
  joinFieldStateText,
  notedFieldSx,
  placeholderFieldSx,
  unavailableFieldSx,
  visuallyHiddenSx,
} from "./reportFieldStates";

const ValueItem = memo(({ item }: { item: MultiValuesCardProps["values"][number] }) => {
  const { unavailableReason } = item;
  const reasonId = useId();
  // The reason ONLY when it actually says something -- SingleValueCard
  // carries why, and the same narrowness applies: the DISPLAY still keys off
  // the raw prop, so a blank reason reads N/A rather than reverting to the
  // figure underneath it.
  const explanation = unavailableReason?.trim() ? unavailableReason : undefined;
  const displayColor = item.color || SingleValueCardColor.PRIMARY;
  // A nullish figure is read FIRST, because the coercion below is the same
  // uncaught throw SingleValueCard carried in a different method: `null` is
  // not a number, so it fell to `item.value.toString()`. The themed rows take
  // their figures from the same report payload through the same unvalidated
  // setters as the summary cards, so the null that blanked the page arrives
  // here too, and this card renders it as N/A for the same reason -- a $0.00
  // or a bare 0 would claim a figure the backend never produced.
  const isMissingValue = item.value === null || item.value === undefined;
  const numericValue = isMissingValue
    ? 0
    : typeof item.value === "number"
      ? item.value
      : parseFloat(item.value!.toString()) || 0;
  // An empty value means "no figure", and it renders BLANK rather than as a
  // number. Every other absent value in these cards is a real 0, so the
  // numeric pipeline turns "" into 0 — which for a figure that could not be
  // produced would assert something false: a participation rate of 0.00%
  // claims the strategy captured none of an upside, and the previous
  // asset's rate left standing would credit it to the wrong ticker. Blank
  // says neither. The field stays editable, so a user can still type one.
  // "No Report" / "NOT IMPLEMENTED" -- the seed this row was handed because no
  // backend figure has replaced it yet, shown VERBATIM. The numeric pipeline
  // below turns it into 0 and formatValue would print "$0.00" or "0" for it,
  // which is the invented figure this state exists to stop; reportFieldStates
  // says which of the two seeds means what.
  const placeholderDisplay = isPlaceholderDisplay(item.value)
    ? String(item.value)
    : null;
  // The nullish N/A sits beside the blank rather than above it, and the two
  // are kept apart on purpose. "" is this card's own shipped sentinel -- the
  // Participation Rate's blank -- and it stays blank. A null was never
  // written deliberately by anything: it is an unread payload where the types
  // promised a number, so it has no shipped display to preserve and reads as
  // the refusal it is.
  const formattedValue =
    placeholderDisplay ??
    (isMissingValue
      ? UNAVAILABLE_DISPLAY
      : item.value === ""
        ? ""
        : formatValue(numericValue, item.type));
  // A reason outranks the blank above: blank alone is the shipped
  // Participation Rate and says only "nothing here", where a reason can say
  // why, so the row stops being a silent gap and starts being an answer.
  const displayValue = unavailableReason ? UNAVAILABLE_DISPLAY : formattedValue;
  // The other thing a row can have to say, and it is the opposite kind of
  // statement from the reason above: a note QUALIFIES a figure the backend
  // produced (a Bucket 3 total measured from the bucket's creation date
  // rather than the period's start), where a reason explains one it refused.
  // Trimmed on the same terms as the reason -- a blank note is no note, and
  // an icon opening an empty tooltip is worse than no icon.
  const trimmedNote = item.note?.trim() ? item.note : undefined;
  // Both ways this row can be showing no figure at all: a seed, and the
  // blank sentinel. Neither is a number, so neither gives a note anything to
  // qualify -- and a mark hung off one would promise an explanation of a
  // figure nobody produced, which is the failure the reason's own blank
  // check above exists to prevent.
  const showsNoFigure =
    placeholderDisplay !== null || item.value === "" || isMissingValue;
  // A reason is the exception, and the only one: it is an answer about THIS
  // run, so when the backend refuses a figure and has something to add about
  // the refusal, the tooltip carries both.
  const note = showsNoFigure && !explanation ? undefined : trimmedNote;
  // One text for one tooltip, reason first when both arrive -- the join and
  // the order live in reportFieldStates because SingleValueCard makes exactly
  // the same call. Its presence, not the reason's, is what puts the icon on
  // screen now.
  const stateText = joinFieldStateText(explanation, note);

  const valueField = (
    <EditableTextField
      value={numericValue}
      formattedValue={displayValue}
      // This card's DISPLAY is deliberately untouched -- it still shows blank
      // for "" and coerces junk to 0. All this says is whether the number
      // beside it is a real figure, so that typing a genuine 0 over a
      // non-figure is not discarded as "unchanged".
      hasFigure={
        item.value !== "" &&
        !isMissingValue &&
        !unavailableReason &&
        placeholderDisplay === null
      }
      type={item.type}
      displayColor={displayColor}
      onValueChange={item.onValueChange as (val: number) => void}
      // Whatever the tooltip says, a screen reader on the input has to hear:
      // a note is as invisible to it as a reason was.
      describedById={stateText ? reasonId : undefined}
      // Only the row that HAS a meaning for an emptied box supplies one;
      // everywhere else this is undefined and clearing writes nothing, as it
      // always has (helpers/types carries which row and why).
      onClear={item.onClear}
    />
  );

  // A row that is a CONTROL rather than a figure renders its control and
  // nothing else: no formatter runs over it, and none of the absent-figure
  // states below apply, because there is no figure here to be absent. The
  // label above it still reads as a label, which is what makes the select
  // between Shares Sold and Sellable Today legible as the thing driving
  // both.
  if (item.control) {
    return (
      <Grid item xs={12} md={item.fullWidthRow ? 12 : 6}>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {item.label}:
          </Typography>
        </Box>
        {item.control}
      </Grid>
    );
  }

  return (
    <Grid item xs={12} md={item.fullWidthRow ? 12 : 6}>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
        {item.labelAction}
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {item.label}:
        </Typography>
      </Box>

      {/* The value is an input, so it CLIPS its own digits rather than
          wrapping when something sits beside it (a stock select, the OEA
          drop's bucket list). Above sm there is room for both on one line,
          which is the layout the cards are designed around; below it the
          neighbour drops to its own line instead of costing the figure
          its digits. */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
      >
        {/* One wrapper, one hidden copy of the text, one tooltip --
            reportFieldStates explains why each of those is needed, and that
            it is the same three whether the text is a reason or a note.

            The WRAPPER is where the two part company. A reason is displaying
            "N/A", so it takes the muting; a note sits beside a real figure,
            which keeps its full weight and its own colour -- muted, it would
            read as unavailable, and that is the one thing a note must not
            say. When both arrive the reason wins the display and the muting
            with it: the field really is showing N/A. */}
        {stateText ? (
          <Tooltip title={stateText} describeChild enterTouchDelay={0}>
            <Box sx={explanation ? unavailableFieldSx : notedFieldSx}>
              <UnavailableReasonIcon />
              {valueField}
              <Box component="span" id={reasonId} sx={visuallyHiddenSx}>
                {stateText}
              </Box>
            </Box>
          </Tooltip>
        ) : placeholderDisplay ? (
          // The same muting N/A gets, for the same reason: a seed left in full
          // figure weight and navy reads as a value. No icon and no tooltip --
          // there is no backend reason behind a seed to explain, and a note
          // that arrived beside one was dropped above. Its own sx
          // rather than N/A's, because a fifteen-character seed does not fit
          // the figure's type size in this card's two-column row.
          <Box sx={placeholderFieldSx}>{valueField}</Box>
        ) : isMissingValue ? (
          // A nullish figure: N/A, muted, and N/A's own sx rather than the
          // seeds' -- reportFieldStates says why two characters keep the
          // figure's type size where a fifteen-character seed cannot. Without
          // the muting the "N/A" would render in the figure's own weight and
          // navy, which is what makes two characters read as a result rather
          // than as the absence of one.
          //
          // No icon and no tooltip: a null arrives carrying nothing, which is
          // precisely the difference between it and the refusal `explanation`
          // handles above. Two rows reach here -- an unread payload where the
          // types promised a number, and the Protect card's Available Margin
          // Balance before anyone types into it -- and neither has a backend
          // sentence behind it to show.
          <Box sx={unavailableFieldSx}>{valueField}</Box>
        ) : (
          valueField
        )}
        {item.action}
      </Box>
    </Grid>
  );
});

const MultiValuesCard = ({
  title,
  values,
  description,
  additionalComponents,
  headerAction,
}: MultiValuesCardProps) => {
  return (
    <Card sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 2 }}>
      <CardContent sx={{ width: "100%" }}>
        <Typography
          variant="h5"
          component="div"
          sx={{
            mb: 2,
            color: "#ffffff",
            backgroundColor: "#003057",
            padding: 1,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {title}
          {headerAction}
        </Typography>
        
        <Grid container spacing={3}>
          {values.map((item, index) => (
            <ValueItem key={index} item={item} />
          ))}
        </Grid>
        
        {additionalComponents}
        
        {description !== "" && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiValuesCard;
